// Conversion ratios
const FL_OZ_TO_ML_RATIO = 29.5735; // 1 US fl oz = 29.5735 ml
const OZ_TO_G_RATIO = 28.349523125; // 1 oz = 28.349523125 g
const CL_TO_ML_RATIO = 10; // 1 cl = 10 ml

export const MIN_AMOUNT_AFTER_CONVERSION = 0.1;

/** Normalize unit names to short form (g, ml, fl oz, etc.) */
export const normalizeUnit = (unit: string): string => {
  if (unit === 'grams') return 'g';
  if (unit === 'milliliters') return 'ml';
  if (unit === 'fl_oz' || unit === 'fluid ounces') return 'fl oz';
  return unit;
};

/** Check if product is solid (weight-based) */
export const isSolidProduct = (unit: string): boolean => {
  const normalized = normalizeUnit(unit);
  return normalized === 'g' || normalized === 'oz';
};

/** Check if product is liquid (volume-based) */
export const isLiquidProduct = (unit: string): boolean => {
  const normalized = normalizeUnit(unit);
  return normalized === 'ml' || normalized === 'cl' || normalized === 'fl oz';
};

/** Get available units for a product based on current unit and app brand */
export const getAvailableUnits = (currentUnit: string, appBrand: string): string[] => {
  const normalized = normalizeUnit(currentUnit);

  if (isSolidProduct(normalized)) {
    return ['g', 'oz'];
  }

  if (isLiquidProduct(normalized)) {
    if (appBrand === 'shestrong') {
      return ['ml', 'cl', 'fl oz'];
    }
    return ['ml', 'fl oz'];
  }

  return [normalized];
};

/** Convert amount from source unit to target unit */
export const convertAmount = (amount: number, sourceUnit: string, targetUnit: string): number => {
  const sourceNormalized = normalizeUnit(sourceUnit);
  const targetNormalized = normalizeUnit(targetUnit);

  if (sourceNormalized === targetNormalized) {
    return amount;
  }

  let converted: number;

  if (sourceNormalized === 'g' && targetNormalized === 'oz') {
    converted = amount / OZ_TO_G_RATIO;
  } else if (sourceNormalized === 'oz' && targetNormalized === 'g') {
    converted = amount * OZ_TO_G_RATIO;
  } else if (sourceNormalized === 'ml' && targetNormalized === 'fl oz') {
    converted = amount / FL_OZ_TO_ML_RATIO;
  } else if (sourceNormalized === 'fl oz' && targetNormalized === 'ml') {
    converted = amount * FL_OZ_TO_ML_RATIO;
  } else if (sourceNormalized === 'cl' && targetNormalized === 'fl oz') {
    const mlAmount = amount * CL_TO_ML_RATIO;
    converted = mlAmount / FL_OZ_TO_ML_RATIO;
  } else if (sourceNormalized === 'fl oz' && targetNormalized === 'cl') {
    const mlAmount = amount * FL_OZ_TO_ML_RATIO;
    converted = mlAmount / CL_TO_ML_RATIO;
  } else if (sourceNormalized === 'cl' && targetNormalized === 'ml') {
    converted = amount * CL_TO_ML_RATIO;
  } else if (sourceNormalized === 'ml' && targetNormalized === 'cl') {
    converted = amount / CL_TO_ML_RATIO;
  } else {
    return amount;
  }

  return Math.max(MIN_AMOUNT_AFTER_CONVERSION, converted);
};

/** Shorten unit for display (grams -> g, milliliters -> ml, etc.) */
export const shortenUnit = (unit: string): string => {
  if (unit === 'grams') return 'g';
  if (unit === 'milliliters') return 'ml';
  if (unit === 'oz') return 'oz';
  if (unit === 'fl_oz') return 'fl oz';
  return unit;
};
